import { MAX_IMPORT_CASES, type TestCaseImportResultDto } from '@tms/shared';
import { Download, FileSpreadsheet, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Button,
  Dialog,
  Field,
  SelectMenu,
  SelectMenuItem,
  Table,
  TableWrap,
  Td,
  Th,
  Tr,
} from '../../../components/ui';
import { translateServerMessage, useErrorText, useT, type MessageKey } from '../../../i18n';
import { flattenFolders, useFolderTree } from '../../folders/api';
import { useImportTestCases } from '../api';
import {
  buildCases,
  detectMapping,
  detectStepLayout,
  IMPORT_FIELDS,
  type ColumnMapping,
  type ImportField,
  type StepLayout,
} from './mapping';
import { ImportFileError, readImportFile, type SheetTable } from './parse-file';

/** A select value is a string, so "no column" needs a sentinel of its own. */
const NO_COLUMN = 'none';
const PREVIEW_ROWS = 5;

const FIELD_LABELS: Record<ImportField, MessageKey> = {
  title: 'cases.importFieldTitle',
  description: 'cases.importFieldDescription',
  preconditions: 'cases.importFieldPreconditions',
  testData: 'cases.importFieldTestData',
  stepAction: 'cases.importFieldStepAction',
  stepExpected: 'cases.importFieldStepExpected',
  priority: 'cases.importFieldPriority',
  labels: 'cases.importFieldLabels',
  environment: 'cases.importFieldEnvironment',
  layer: 'cases.importFieldLayer',
  module: 'cases.importFieldModule',
  groupKey: 'cases.importFieldGroupKey',
};

/** What the report says about each list the import had to grow. */
const CREATED_LABELS: Record<keyof TestCaseImportResultDto['created'], MessageKey> = {
  labels: 'cases.importCreatedLabels',
  environments: 'cases.importCreatedEnvironments',
  layers: 'cases.importCreatedLayers',
  modules: 'cases.importCreatedModules',
};

export interface ImportCasesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectCode: string;
  /** Folder to start from, when the list already has one selected. */
  defaultFolderId?: string | undefined;
}

/**
 * Imports test cases written in another system.
 *
 * The file is read here rather than uploaded: the person sees what was
 * understood — which column is which, how many cases came out — before
 * anything is created, and the API receives test cases instead of a file
 * format it would have to learn.
 */
export const ImportCasesDialog = ({
  open,
  onOpenChange,
  projectCode,
  defaultFolderId,
}: ImportCasesDialogProps) => {
  const { t } = useT();
  const { errorText } = useErrorText();
  const { data: folders } = useFolderTree(projectCode, 'case');
  const importCases = useImportTestCases(projectCode);
  const fileInput = useRef<HTMLInputElement>(null);

  const [fileName, setFileName] = useState('');
  const [table, setTable] = useState<SheetTable | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping | null>(null);
  const [layout, setLayout] = useState<StepLayout>('rows');
  const [folderId, setFolderId] = useState(defaultFolderId ?? '');
  const [fileError, setFileError] = useState('');
  const [result, setResult] = useState<TestCaseImportResultDto | null>(null);

  const folderOptions = flattenFolders(folders ?? []);
  const cases = table && mapping ? buildCases(table, mapping, layout) : [];
  const tooMany = cases.length > MAX_IMPORT_CASES;
  const canImport =
    cases.length > 0 && !tooMany && mapping?.title !== null && folderId !== '' && !importCases.isPending;

  const reset = () => {
    setFileName('');
    setTable(null);
    setMapping(null);
    setFileError('');
    setResult(null);
  };

  const closeDialog = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setFileError('');
    try {
      const parsed = await readImportFile(file);
      const detected = detectMapping(parsed.headers);
      setTable(parsed);
      setMapping(detected);
      setLayout(detectStepLayout(parsed, detected));
      setFileName(file.name);
      if (folderId === '') setFolderId(defaultFolderId ?? folderOptions[0]?.id ?? '');
    } catch (error) {
      setTable(null);
      setMapping(null);
      setFileError(
        error instanceof ImportFileError
          ? t(`cases.importFile_${error.reason}`)
          : t('cases.importFile_unreadable'),
      );
    }
  };

  const submit = async () => {
    try {
      setResult(await importCases.mutateAsync({ folderId, cases }));
    } catch (error) {
      toast.error(errorText(error, 'cases.importFailed'));
    }
  };

  const downloadReport = () => {
    if (!result) return;
    const rows = [
      [t('cases.importRow'), t('common.title'), t('cases.importReason')],
      ...result.skipped.map((row) => [
        String(row.sourceRow ?? ''),
        row.title ?? '',
        translateServerMessage(row.message, t) ?? row.message,
      ]),
    ];
    // Quoted everywhere: titles and messages carry commas and quotes.
    const csv = rows
      .map((cells) => cells.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `import-report-${projectCode}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={closeDialog}
      title={t('cases.importTitle')}
      description={result ? undefined : t('cases.importHint')}
      size="lg"
      footer={
        result ? (
          <Button variant="primary" onClick={() => closeDialog(false)}>
            {t('common.done')}
          </Button>
        ) : (
          <>
            <Button
              variant="secondary"
              onClick={() => closeDialog(false)}
              disabled={importCases.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={() => void submit()}
              loading={importCases.isPending}
              disabled={!canImport}
            >
              {t('cases.importSubmit')}
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-col gap-4">
        {result ? (
          <ImportReport result={result} onDownload={downloadReport} />
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-border px-4 py-3">
              <FileSpreadsheet className="size-4 shrink-0 text-ink-subtle" />
              <span className="min-w-0 flex-1 truncate text-[13px] text-ink">
                {fileName || t('cases.importNoFile')}
              </span>
              <Button variant="secondary" size="sm" onClick={() => fileInput.current?.click()}>
                <Upload className="size-3.5" />
                {fileName ? t('cases.importAnotherFile') : t('cases.importPick')}
              </Button>
              {/* In flow and sized to nothing: an absolutely positioned input
                  stretches the scroll container of the dialog. */}
              <input
                ref={fileInput}
                type="file"
                accept=".csv,.tsv,.txt,.xlsx,.xlsm"
                className="size-0 overflow-hidden border-0 p-0"
                aria-hidden="true"
                tabIndex={-1}
                onChange={(event) => {
                  void onFile(event.target.files?.[0]);
                  event.target.value = '';
                }}
              />
            </div>

            {fileError ? (
              <p role="alert" className="text-[13px] text-fail">
                {fileError}
              </p>
            ) : null}

            {table && mapping ? (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label={t('common.folder')} required>
                    {({ id }) => (
                      <SelectMenu
                        id={id}
                        value={folderId}
                        onValueChange={setFolderId}
                        placeholder={t('cases.folderPlaceholder')}
                      >
                        {folderOptions.map((option) => (
                          <SelectMenuItem key={option.id} value={option.id}>
                            {option.label}
                          </SelectMenuItem>
                        ))}
                      </SelectMenu>
                    )}
                  </Field>

                  <Field label={t('cases.importStepsLayout')}>
                    {({ id }) => (
                      <SelectMenu
                        id={id}
                        value={layout}
                        onValueChange={(value) => setLayout(value as StepLayout)}
                      >
                        <SelectMenuItem value="rows">{t('cases.importStepsRows')}</SelectMenuItem>
                        <SelectMenuItem value="cell">{t('cases.importStepsCell')}</SelectMenuItem>
                      </SelectMenu>
                    )}
                  </Field>

                  {IMPORT_FIELDS.map((field) => (
                    <Field
                      key={field}
                      label={t(FIELD_LABELS[field])}
                      required={field === 'title'}
                      error={
                        field === 'title' && mapping.title === null
                          ? t('cases.importNoTitle')
                          : undefined
                      }
                    >
                      {({ id }) => (
                        <SelectMenu
                          id={id}
                          value={mapping[field] === null ? NO_COLUMN : String(mapping[field])}
                          onValueChange={(value) =>
                            setMapping({
                              ...mapping,
                              [field]: value === NO_COLUMN ? null : Number(value),
                            })
                          }
                        >
                          <SelectMenuItem value={NO_COLUMN}>
                            {t('cases.importColumnNone')}
                          </SelectMenuItem>
                          {table.headers.map((header, index) => (
                            <SelectMenuItem key={`${header}-${index}`} value={String(index)}>
                              {header || t('cases.importColumnUnnamed', { index: index + 1 })}
                            </SelectMenuItem>
                          ))}
                        </SelectMenu>
                      )}
                    </Field>
                  ))}
                </div>

                <ImportPreview cases={cases} tooMany={tooMany} />
                <p className="text-[12px] text-ink-subtle">{t('cases.importDefaults')}</p>
              </>
            ) : null}
          </>
        )}
      </div>
    </Dialog>
  );
};

const ImportPreview = ({
  cases,
  tooMany,
}: {
  cases: ReturnType<typeof buildCases>;
  tooMany: boolean;
}) => {
  const { t } = useT();

  if (cases.length === 0) {
    return <p className="text-[13px] text-ink-muted">{t('cases.importEmptyResult')}</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-[13px] text-ink">{t('cases.importPreview', { count: cases.length })}</p>
      {tooMany ? (
        <p role="alert" className="text-[13px] text-fail">
          {t('cases.importTooMany', { max: MAX_IMPORT_CASES })}
        </p>
      ) : null}
      <TableWrap className="rounded-lg border border-border">
        <Table>
          <thead>
            <Tr>
              <Th className="w-16">{t('cases.importRow')}</Th>
              <Th>{t('common.title')}</Th>
              <Th className="w-24">{t('cases.importStepCount')}</Th>
            </Tr>
          </thead>
          <tbody>
            {cases.slice(0, PREVIEW_ROWS).map((candidate) => (
              <Tr key={candidate.sourceRow}>
                <Td className="tabular-nums text-ink-subtle">{candidate.sourceRow}</Td>
                <Td>
                  <span className="line-clamp-2">
                    {candidate.title || t('cases.importMissingTitle')}
                  </span>
                </Td>
                <Td className="tabular-nums">{candidate.steps.length}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </TableWrap>
    </div>
  );
};

const ImportReport = ({
  result,
  onDownload,
}: {
  result: TestCaseImportResultDto;
  onDownload: () => void;
}) => {
  const { t } = useT();

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[13px] text-ink">
        {t('cases.importDone', { count: result.imported.length })}
      </p>

      {/* Growing the project's own lists is a side effect of the import, so
          it is named rather than left to be discovered in the settings. */}
      {(Object.keys(CREATED_LABELS) as Array<keyof typeof CREATED_LABELS>).map((list) =>
        result.created[list].length === 0 ? null : (
          <p key={list} className="text-[13px] text-ink-muted">
            {t(CREATED_LABELS[list], { names: result.created[list].join(', ') })}
          </p>
        ),
      )}

      {result.skipped.length === 0 ? null : (
        <>
          <div className="flex items-center justify-between gap-3">
            <p className="text-[13px] text-fail">
              {t('cases.importSkipped', { count: result.skipped.length })}
            </p>
            <Button variant="secondary" size="sm" onClick={onDownload}>
              <Download className="size-3.5" />
              {t('cases.importReport')}
            </Button>
          </div>
          <TableWrap className="max-h-64 overflow-y-auto rounded-lg border border-border">
            <Table>
              <thead>
                <Tr>
                  <Th className="w-16">{t('cases.importRow')}</Th>
                  <Th>{t('common.title')}</Th>
                  <Th>{t('cases.importReason')}</Th>
                </Tr>
              </thead>
              <tbody>
                {result.skipped.map((row, index) => (
                  <Tr key={`${row.sourceRow ?? index}-${index}`}>
                    <Td className="tabular-nums text-ink-subtle">{row.sourceRow ?? '—'}</Td>
                    <Td>{row.title ?? '—'}</Td>
                    <Td className="text-ink-muted">
                      {translateServerMessage(row.message, t) ?? row.message}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </>
      )}
    </div>
  );
};
